export type FigureDomain =
  | "workforce"
  | "network"
  | "behavioral"
  | "stroke"
  | "screening"
  | "market"
  | "rhtp";

export type FigureMode = "MEASURED" | "DIRECT" | "FIELD-VERIFIED" | "MODELED";

export interface Figure {
  id: string;
  domain: FigureDomain;
  label: string;
  value: string;
  detail?: string;
  source: string;
  sourceUrl?: string;
  retrievedOn: string;
  mode: FigureMode;
  note?: string;
}

export const modeDefinitions: Record<FigureMode, string> = {
  MEASURED: "computed by us from record-level data",
  DIRECT: "published by a named authority, cited with retrieval date",
  "FIELD-VERIFIED": "reported by program leadership, corroboration pending",
  MODELED: "a scenario or projection, never presented as measured",
};

const JABSOM =
  "JABSOM Area Health Education Center, Hawaiʻi Physician Workforce Assessment 2025";
const HHDW = "Hawaiʻi Health Data Warehouse, indicator 1307";

export const figures: Figure[] = [
  {
    id: "1",
    domain: "workforce",
    label: "Physician shortage, statewide",
    value: "644 FTE",
    detail: "832.6 FTE island-adjusted",
    source: JABSOM,
    retrievedOn: "2026-09-06",
    mode: "DIRECT",
    note: "Preliminary 2026 figures were expected 12 September 2026; this row is flagged for update when they are published.",
  },
  {
    id: "2",
    domain: "workforce",
    label: "Hawaiʻi County shortage, all specialties",
    value: "43%",
    detail: "223.6 FTE, up from 201 FTE in 2024",
    source: JABSOM,
    retrievedOn: "2026-09-06",
    mode: "DIRECT",
  },
  {
    id: "3",
    domain: "workforce",
    label: "Maui County shortage, all specialties",
    value: "41%",
    detail: "179.2 FTE",
    source: JABSOM,
    retrievedOn: "2026-09-06",
    mode: "DIRECT",
  },
  {
    id: "4",
    domain: "workforce",
    label: "Maui County primary care shortage",
    value: "35%",
    detail:
      "45.0 FTE, the highest primary care shortfall by percentage in the state",
    source: JABSOM,
    retrievedOn: "2026-09-06",
    mode: "DIRECT",
  },
  {
    id: "5",
    domain: "workforce",
    label: "Residents in primary care shortage areas",
    value: "604,628",
    detail: "34 HPSA designations; 67.05% of need met",
    source: "HRSA, Designated HPSA Quarterly Summary",
    retrievedOn: "2026-06-30",
    mode: "DIRECT",
  },
  {
    id: "6",
    domain: "network",
    label: "Insurer directory overstatement of primary care, statewide",
    value: "+107%",
    detail:
      "Listed clinicians against a verified truth set of 1,994 practicing primary care clinicians (Cut B, the milder accurate figure; the physician-only cut is +180%)",
    source: "HHIP over HMSA Report 403 (DHS UIPA), DCCA PVL, NPPES",
    retrievedOn: "2026-05-05",
    mode: "MEASURED",
  },
  {
    id: "7",
    domain: "network",
    label: "Directory validation funnel",
    value: "4,867 → 946",
    detail:
      "4,867 raw rows, 2,738 unique NPIs, 1,260 validated primary care clinicians, 946 accepting new patients",
    source: "Report 403 roster (Department of Human Services public-records response), NPI de-duplication and the CMS NPI Registry taxonomy check, and plan-filing accepting-new-patients flags",
    retrievedOn: "2026-05-05",
    mode: "MEASURED",
  },
  {
    id: "8",
    domain: "network",
    label: "Carrier assessment cap, unchanged in over two decades",
    value: "$1,000,000",
    detail:
      "Aggregate annual assessment on health carriers for network regulation",
    source: "HRS 431:2-216(a) and (b)",
    retrievedOn: "2026-08-13",
    mode: "DIRECT",
  },
  {
    id: "9",
    domain: "behavioral",
    label: "Adult civil acute psychiatric beds",
    value: "147",
    detail:
      "10.2 per 100,000 residents, net of forensic and youth-only beds; consensus minimum benchmark 30 per 100,000",
    source:
      "SHPDA 2023 Healthcare Utilization Report, Table 8; Census Vintage 2024 denominator",
    retrievedOn: "2026-08-02",
    mode: "MEASURED",
  },
  {
    id: "10",
    domain: "stroke",
    label: "Maui County stroke death rate",
    value: "45.7 per 100,000",
    detail:
      "Age-adjusted, 2022 to 2024; the 2021 to 2023 rate was 51.1; Healthy People 2030 target 33.4",
    source: HHDW,
    retrievedOn: "2026-08-13",
    mode: "DIRECT",
  },
  {
    id: "11",
    domain: "stroke",
    label: "Hawaiʻi County stroke death rate",
    value: "42.8 per 100,000",
    detail: "Age-adjusted, 2022 to 2024; Honolulu 36.4; Kauaʻi 31.6",
    source: HHDW,
    retrievedOn: "2026-08-13",
    mode: "DIRECT",
  },
  {
    id: "12",
    domain: "screening",
    label:
      "Mammography screening and distance to the nearest certified facility, Hawaiʻi Island",
    value: "r = −0.50",
    detail:
      "0.068 percentage points of screening lost per mile (p = 0.010), 26 ZIP tabulation areas; ecological, community-level",
    source:
      "HHTF and HHIP analysis over CDC PLACES 2022 and FDA MQSA facility locations",
    retrievedOn: "2026-08-11",
    mode: "MEASURED",
  },
  {
    id: "13",
    domain: "screening",
    label: "Mammography screening, women 50 to 74",
    value: "69.8% vs 79.5%",
    detail:
      "Hawaiʻi County against Honolulu, age-adjusted; a modeled small-area estimate, not a direct count",
    source: "CDC PLACES 2022",
    retrievedOn: "2026-08-11",
    mode: "DIRECT",
  },
  {
    id: "14",
    domain: "market",
    label: "Commercial insurance market concentration (HHI)",
    value: "3,138",
    detail:
      "HMSA 49.2% of member months; HMSA and Kaiser together 74.1%; a national state average is near 2,300",
    source: "HHIP computed from NAIC Market Share Reports, plan year 2024",
    retrievedOn: "2026-05-01",
    mode: "MEASURED",
  },
  {
    id: "15",
    domain: "rhtp",
    label: "Rural Health Transformation Program award to Hawaiʻi",
    value: "$188.9M",
    detail:
      "$188,892,439.75 for the first budget period; five annual awards depend on federal reporting and award decisions",
    source:
      "CMS award announcement, 29 December 2025; Governor's release, 6 January 2026",
    retrievedOn: "2026-09-10",
    mode: "DIRECT",
  },
  {
    id: "16",
    domain: "rhtp",
    label: "RHTP funds announced to date",
    value: "$58M",
    detail:
      "$45M to JABSOM HOME RUN workforce awards and $13M to the Department of Health for ambulances and emergency communications",
    source: "Office of the Governor release, 1 September 2026",
    retrievedOn: "2026-09-10",
    mode: "DIRECT",
  },
];
